/* AUTO-SYNCED from PsyKit.io/components/session/EmotionPicker.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import { useState } from "react";

export const DEFAULT_EMOTIONS_DISTRESS: string[] = [
  "depressed",
  "anxious",
  "fearful",
  "angry",
  "guilty",
  "powerless",
  "embarrassed",
  "ashamed",
  "jealous",
  "hopeless",
  "obsessed",
  "foolish",
  "insecure",
  "worthless",
  "hurt",
  "mistrustful",
  "irritable",
  "incompetent",
  "lonely",
  "sad",
  "confused",
  "indecisive",
  "frustrated",
  "deceived",
  "different",
  "overwhelmed",
  "numb",
];

export const DEFAULT_EMOTIONS_POSITIVE: string[] = [
  "accepting",
  "alert",
  "amused",
  "at ease",
  "awed",
  "calm",
  "clear-headed",
  "confident",
  "content",
  "curious",
  "empowered",
  "energetic",
  "enlightened",
  "enthusiastic",
  "excited",
  "focused",
  "free",
  "grateful",
  "guilt-free",
  "happy",
  "hopeful",
  "inspired",
  "joy",
  "joyful",
  "light",
  "motivated",
  "peaceful",
  "pleased",
  "pleasant",
  "positive",
  "positive self-image",
  "proud",
  "purposeful",
  "relaxed",
  "relief",
  "relieved",
  "renewed",
  "resilient",
  "safe",
  "serene",
  "silly",
  "strong",
  "surprised",
  "worthy",
  "grounded",
];

export const DEFAULT_EMOTIONS: string[] = [
  ...DEFAULT_EMOTIONS_DISTRESS,
  ...DEFAULT_EMOTIONS_POSITIVE,
];

// ── Valence classification ─────────────────────────────────────────────────
// The negative/positive split is the single source of truth for the
// red "Negative" / green "Positive" framing used by both the picker and the
// read-only CheckinComparison summary. Custom (user-added) emotions are
// "other" — they carry no clinical valence so we render them neutrally.

const POSITIVE_SET = new Set(DEFAULT_EMOTIONS_POSITIVE);
const NEGATIVE_SET = new Set(DEFAULT_EMOTIONS_DISTRESS);

export type EmotionValence = "positive" | "negative" | "other";

export function emotionValence(emotion: string): EmotionValence {
  if (NEGATIVE_SET.has(emotion)) return "negative";
  if (POSITIVE_SET.has(emotion)) return "positive";
  return "other";
}

/** Split a list of emotions into negative / positive / other, preserving order. */
export function classifyEmotions(list: string[]): {
  negative: string[];
  positive: string[];
  other: string[];
} {
  const negative: string[] = [];
  const positive: string[] = [];
  const other: string[] = [];
  for (const e of list) {
    const v = emotionValence(e);
    if (v === "negative") negative.push(e);
    else if (v === "positive") positive.push(e);
    else other.push(e);
  }
  return { negative, positive, other };
}

// ── Corner valence markers (red Negative / green Positive) ──────────────────

const NEGATIVE_RED = "#C84444";
const NEGATIVE_RIM = "#8B2E2E";
const POSITIVE_GREEN = "#5FAE78";
const POSITIVE_RIM = "#2E6B3E";

export function ValenceMarker({ kind }: { kind: "negative" | "positive" }) {
  const isNeg = kind === "negative";
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.16em]"
      style={{
        color: isNeg ? NEGATIVE_RED : POSITIVE_GREEN,
        border: `1px solid ${isNeg ? NEGATIVE_RIM : POSITIVE_RIM}`,
        background: isNeg ? "rgba(200,68,68,0.06)" : "rgba(95,174,120,0.06)",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: isNeg ? NEGATIVE_RED : POSITIVE_GREEN,
        }}
      />
      {isNeg ? "Negative" : "Positive"}
    </span>
  );
}

interface EmotionPickerProps {
  emotions?: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

export default function EmotionPicker({
  emotions = DEFAULT_EMOTIONS,
  selected,
  onChange,
}: EmotionPickerProps) {
  const [customEmotions, setCustomEmotions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");

  const allEmotions = [...emotions, ...customEmotions];
  const { negative, positive } = classifyEmotions(allEmotions);
  // Anything not in the built-in valence sets (custom additions, plus any
  // already-selected stragglers) renders in a neutral "Your own" strip.
  const otherEmotions = Array.from(
    new Set([
      ...customEmotions,
      ...selected.filter((e) => emotionValence(e) === "other"),
    ])
  );

  const toggle = (emotion: string) => {
    if (selected.includes(emotion)) {
      onChange(selected.filter((e) => e !== emotion));
    } else {
      onChange([...selected, emotion]);
    }
  };

  const handleAdd = () => {
    const trimmed = inputValue.trim().toLowerCase();
    if (!trimmed || allEmotions.includes(trimmed)) {
      setInputValue("");
      return;
    }
    setCustomEmotions((prev) => [...prev, trimmed]);
    onChange([...selected, trimmed]); // auto-select on add
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const renderChip = (emotion: string, i: number) => {
    const isSelected = selected.includes(emotion);
    const isCustom = customEmotions.includes(emotion);
    return (
      <button
        key={`${i}-${emotion}`}
        type="button"
        onClick={() => toggle(emotion)}
        className="rounded-[4px] font-[var(--font-barlow)] text-[13px] uppercase tracking-[0.06em] px-3 py-2 transition-all duration-100"
        style={{
          background: isSelected ? "#C9A84C22" : "transparent",
          border: `1px solid ${isSelected ? "#C9A84C" : isCustom ? "#3D5470" : "#3A5270"}`,
          color: isSelected ? "#C9A84C" : "#8AABC8",
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            (e.currentTarget as HTMLButtonElement).style.borderColor = "#5A7A98";
            (e.currentTarget as HTMLButtonElement).style.color = "#B0C8E0";
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            (e.currentTarget as HTMLButtonElement).style.borderColor = isCustom ? "#3D5470" : "#3A5270";
            (e.currentTarget as HTMLButtonElement).style.color = "#8AABC8";
          }
        }}
      >
        {emotion}
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Valence-grouped chips: Negative (red, top) → divider → Positive
          (green, bottom). The corner markers orient which side is which. */}
      <div
        className="rounded-[4px] px-3 py-3"
        style={{ border: "1px solid #1A2E4A", background: "#070C16" }}
      >
        {/* Negative — red marker pinned top-right */}
        <div className="flex justify-end">
          <ValenceMarker kind="negative" />
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {negative.map((e, i) => renderChip(e, i))}
        </div>

        {/* Divider between the two valences */}
        <div className="my-3 border-t" style={{ borderColor: "#233D6B" }} />

        {/* Positive — green marker pinned bottom-right */}
        <div className="flex flex-wrap gap-2">
          {positive.map((e, i) => renderChip(e, i + negative.length))}
        </div>
        <div className="flex justify-end mt-2">
          <ValenceMarker kind="positive" />
        </div>
      </div>

      {/* Custom / "your own" emotions — neutral, no valence */}
      {otherEmotions.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span
            className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.16em]"
            style={{ color: "#5A7A98" }}
          >
            Your own
          </span>
          <div className="flex flex-wrap gap-2">
            {otherEmotions.map((e, i) => renderChip(e, i + negative.length + positive.length))}
          </div>
        </div>
      )}

      {/* Add custom emotion */}
      <div className="flex gap-2 pt-1" style={{ borderTop: "1px solid #1A2E4A" }}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add your own emotion…"
          maxLength={40}
          className="flex-1 rounded-[4px] font-[var(--font-barlow)] text-[12px] px-3 py-2 outline-none"
          style={{
            background: "#080C14",
            border: "1px solid #233D6B",
            color: "#C8D4E0",
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = "#C9A84C44"; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "#233D6B"; }}
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
          className="px-4 py-2 rounded-[4px] font-[var(--font-barlow)] text-[11px] uppercase tracking-[0.1em] border transition-all disabled:opacity-30"
          style={{
            borderColor: "#C9A84C",
            color: "#C9A84C",
            background: "transparent",
          }}
          onMouseEnter={(e) => {
            if (inputValue.trim()) {
              (e.currentTarget as HTMLButtonElement).style.background = "#C9A84C";
              (e.currentTarget as HTMLButtonElement).style.color = "#080C14";
            }
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "transparent";
            (e.currentTarget as HTMLButtonElement).style.color = "#C9A84C";
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}
